import React, { useRef } from 'react';
import { useStatistics } from '../hooks/useStatistics';
import ActivityHeatmap from './ActivityHeatmap';
import TimeOfDayChart from './TimeOfDayChart';
import { enhancedStatisticsService } from '../services/enhancedStatisticsService';

const StatisticsPage: React.FC = () => {
  const { stats, dailyStats, sessions, isLoading, refreshStats, formatTime, formatDate } = useStatistics();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExportStats = async () => {
    try {
      await enhancedStatisticsService.exportStatistics();
    } catch (error) {
      alert('Failed to export statistics');
    }
  };

  const handleImportStats = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const success = await enhancedStatisticsService.importStatistics(file, 'merge');
      if (success) {
        alert('Statistics imported successfully!');
        refreshStats();
      } else {
        alert('Failed to import statistics');
      }
    } catch (error) {
      alert('Invalid backup file');
    }

    // Clear the input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading statistics...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400">No statistics available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => window.location.hash = '#/'}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Editor
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            📊 Writing Statistics
          </h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={refreshStats}
            className="px-4 py-2 bg-primary hover:bg-primary/80 text-white rounded-lg transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
          
          <button
            onClick={handleExportStats}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export
          </button>
          
          <button
            onClick={handleImportStats}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            Import
          </button>
          
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".json"
            className="hidden"
          />
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Total Sessions</h3>
          <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{stats.totalSessions}</p>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Total Words Saved</h3>
          <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{stats.totalWords.toLocaleString()}</p>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Total Characters</h3>
          <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{stats.totalCharacters.toLocaleString()}</p>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Total Time</h3>
          <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{formatTime(stats.totalTime)}</p>
        </div>
      </div>

      {/* Performance & Streaks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Performance Metrics */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">📈 Performance</h2>
          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Words per Minute</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{stats.wordsPerMinute}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Average Words/Session</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{stats.averageWordsPerSession}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Average Session Length</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{formatTime(stats.averageSessionLength)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Longest Session</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{formatTime(stats.longestSession)}</span>
            </div>
          </div>
        </div>

        {/* Streaks */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">🔥 Streaks</h2>
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">Current Streak</p>
              <p className="text-4xl font-bold text-orange-500">{stats.currentStreak}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">days</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">Longest Streak</p>
              <p className="text-2xl font-bold text-green-500">{stats.longestStreak}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">days</p>
            </div>
          </div>
        </div>
      </div>

              {/* Deletion Analytics */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">✂️ Get Deleto Activity</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">Times Got Deleto'd</p>
              <p className="text-2xl font-bold text-red-600">{stats.totalTimesDeleto.toLocaleString()}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">Characters Deleted</p>
              <p className="text-2xl font-bold text-red-500">{stats.totalDeletedChars.toLocaleString()}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">Words Deleted</p>
              <p className="text-2xl font-bold text-red-500">{stats.totalDeletedWords.toLocaleString()}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">Deletion Rate</p>
              <p className="text-2xl font-bold text-yellow-500">
                {stats.totalCharacters > 0 ? Math.round((stats.totalDeletedChars / stats.totalCharacters) * 100) : 0}%
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">Net Efficiency</p>
              <p className="text-2xl font-bold text-blue-500">
                {stats.totalCharacters > 0 ? Math.round(((stats.totalCharacters - stats.totalDeletedChars) / stats.totalCharacters) * 100) : 0}%
              </p>
            </div>
          </div>
        </div>

      {/* Most Productive Day */}
      {stats.mostProductiveDay && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">🏆 Most Productive Day</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">Date</p>
              <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {formatDate(stats.mostProductiveDay.date)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">Words Written</p>
              <p className="text-lg font-bold text-green-500">{stats.mostProductiveDay.totalWords}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">Sessions</p>
              <p className="text-lg font-bold text-blue-500">{stats.mostProductiveDay.sessions}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">Time Spent</p>
              <p className="text-lg font-bold text-purple-500">{formatTime(stats.mostProductiveDay.totalTime)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Time of Day */}
      <TimeOfDayChart sessions={sessions} formatTime={formatTime} />

      {/* Activity Heatmap */}
      <div className="mb-8">
        <ActivityHeatmap 
          dailyStats={dailyStats}
          formatTime={formatTime}
          formatDate={formatDate}
        />
      </div>

      {/* Individual Sessions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">📝 All Sessions</h2>
        {sessions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-2 px-4 font-medium text-gray-900 dark:text-gray-100">Date & Time</th>
                  <th className="text-center py-2 px-4 font-medium text-gray-900 dark:text-gray-100">Words</th>
                  <th className="text-center py-2 px-4 font-medium text-gray-900 dark:text-gray-100">Characters</th>
                  <th className="text-center py-2 px-4 font-medium text-gray-900 dark:text-gray-100">Duration</th>
                  <th className="text-center py-2 px-4 font-medium text-gray-900 dark:text-gray-100"># Times Deleto</th>
                  <th className="text-center py-2 px-4 font-medium text-gray-900 dark:text-gray-100">Status</th>
                </tr>
              </thead>
              <tbody>
                {sessions
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                  .map((session) => {
                    const sessionDate = new Date(session.date);
                    // Status based on explicit flags
                    const isCompleted = !!session.sessionCompleted;
                    const isEndedEarly = !!session.sessionEndedEarly;
                    const statusIcon = isCompleted ? '✅' : (isEndedEarly ? '⚠️' : '✅');
                    const statusText = isCompleted ? 'Session completed' : (isEndedEarly ? 'Session ended early' : 'Session completed');
                    
                    return (
                      <tr key={session.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="py-3 px-4 text-gray-900 dark:text-gray-100">
                          <div className="text-sm">
                            <div className="font-medium">{formatDate(session.date)}</div>
                            <div className="text-gray-500 dark:text-gray-400">
                              {sessionDate.toLocaleTimeString('en-US', { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center text-gray-600 dark:text-gray-400">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                            {session.wordCount || 0}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center text-gray-600 dark:text-gray-400">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                            {session.charCount || 0}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center text-gray-600 dark:text-gray-400">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                            {formatTime(session.sessionDuration || 0)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center text-gray-600 dark:text-gray-400">
                          {(session.numTimesDeleto || 0) > 0 ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200">
                              {session.numTimesDeleto}x
                            </span>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                                title={statusText}>
                            {statusIcon}
                            <span className="hidden sm:inline">{statusText}</span>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-600 dark:text-gray-400 text-center py-4">No sessions found. Start writing to see your sessions here!</p>
        )}
      </div>
    </div>
  );
};

export default StatisticsPage; 